import React, { useState } from "react";
import {
	Box,
	Typography,
	Grid,
	Chip,
	Button,
	Card,
	CardMedia,
	CardContent,
	Skeleton,
	IconButton,
	CircularProgress,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EastIcon from "@mui/icons-material/East";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MapIcon from "@mui/icons-material/Map";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PublicIcon from "@mui/icons-material/Public";
import travelkitCover from "../../assets/travel-kit.png";
import { GUIDE_COVER_TITLES } from "../../data/guideCatalogImages";

const CoverPlaceName = ({ guideId }) => {
	const style = GUIDE_COVER_TITLES[guideId];
	if (!style) return null;
	const len = style.label.length;
	const fontSize = len > 13 ? 34 : len > 9 ? 42 : 52;
	const strokeWidth = len > 13 ? 10 : 12;
	return (
		<Box
			aria-hidden
			sx={{
				position: "absolute",
				left: 0,
				right: 0,
				top: { xs: 28, sm: 36 },
				pointerEvents: "none",
				zIndex: 2,
				px: 0.5,
			}}
		>
			<Box
				component="svg"
				viewBox="0 0 400 84"
				sx={{
					width: "100%",
					height: "auto",
					display: "block",
					overflow: "visible",
				}}
			>
				<text
					x="200"
					y="50"
					textAnchor="middle"
					dominantBaseline="middle"
					fontFamily="'Titan One', cursive"
					fontSize={fontSize}
					fill={style.fill}
					stroke={style.stroke}
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeLinejoin="round"
					paintOrder="stroke"
				>
					{style.label}
				</text>
			</Box>
		</Box>
	);
};

// Mismo sistema que el resto: imports para que el bundler sirva en /static/media/...
const COVER_IMAGE_MAP = {
	travelkit: travelkitCover,
};

/* Tokens del dashboard (alineados con Dashboard.css) */
const DASHBOARD = {
	primary: "#6366F1",
	primaryDark: "#4F46E5",
	textPrimary: "#1a1a2e",
	textMuted: "#64748B",
	border: "rgba(0, 0, 0, 0.06)",
	borderHover: "rgba(99, 102, 241, 0.3)",
	radiusCard: "20px",
	radiusButton: "12px",
	shadowSm: "0 2px 8px rgba(0, 0, 0, 0.04)",
	shadowMd: "0 4px 16px rgba(0, 0, 0, 0.08)",
	bgCard: "#fff",
	bgSubtle: "rgba(0, 0, 0, 0.02)",
	primaryTint: "rgba(99, 102, 241, 0.12)",
	primaryHover: "rgba(99, 102, 241, 0.08)",
};

/* Categorías principales por tipo */
const categories = [
	{ id: "all", label: "Todas", Icon: AutoAwesomeIcon },
	{ id: "destinations", label: "Guías de viaje", Icon: MapIcon },
	{ id: "utility", label: "Utilidades", Icon: AssignmentIcon },
];

/* Sub-filtros por región (aparecen cuando se selecciona "Guías de viaje") */
const regionFilters = [
	{ id: "all", label: "Todos los destinos", emoji: "🌍" },
	{ id: "asia", label: "Asia", emoji: "🏯" },
	{ id: "europa", label: "Europa", emoji: "🏰" },
	{ id: "america", label: "América", emoji: "🗽" },
];

const GuideCardDashboard = ({
	guide,
	onViewGuide,
	onCustomizeGuide,
	onPurchaseGuide,
	onComingSoonClick,
	purchasingGuideId,
	isFavorite = false,
	onToggleFavorite = null,
	isLoggedIn = false,
}) => {
	const {
		id,
		name,
		coverImage: coverFromGuide,
		days,
		description,
		tags = [],
		category,
		isFree,
		price,
		userHasAccess,
		availableForPurchase = true,
	} = guide;
	const isComingSoon = availableForPurchase === false;
	// Travel Kit se muestra como guía gratis (badge "Gratis" y acceso sin compra)
	const isFreeOrTravelKit =
		isFree || category === "travelkit" || id === "travelkit";
	// Usar imagen del padre si la envía; si no, fallback a COVER_IMAGE_MAP (todo-viaje, travelkit)
	const coverImage = coverFromGuide || COVER_IMAGE_MAP[id];
	const isPurchasing = purchasingGuideId === id;

	// Color palette based on destination or type
	const getColorConfig = (guideName, guideCategory) => {
		// Colores especiales para plantillas de utilidad
		if (guideCategory === "utility") {
			return {
				gradient: "linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)",
				badge: "#6C5CE7",
				light: "rgba(108, 92, 231, 0.1)",
			};
		}
		// Colores especiales para Travel Kit
		if (guideCategory === "travelkit") {
			return {
				gradient: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
				badge: "#06B6D4",
				light: "rgba(6, 182, 212, 0.1)",
			};
		}

		const configs = {
			kioto: {
				gradient: "linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)",
				badge: "#F43F5E",
				light: "rgba(244, 63, 94, 0.1)",
			},
			tokio: {
				gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
				badge: "#EF4444",
				light: "rgba(239, 68, 68, 0.1)",
			},
			paris: {
				gradient: "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
				badge: "#6366F1",
				light: "rgba(99, 102, 241, 0.1)",
			},
			londres: {
				gradient: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
				badge: "#3B82F6",
				light: "rgba(59, 130, 246, 0.1)",
			},
			ny: {
				gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
				badge: "#8B5CF6",
				light: "rgba(139, 92, 246, 0.1)",
			},
			nueva: {
				gradient: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
				badge: "#8B5CF6",
				light: "rgba(139, 92, 246, 0.1)",
			},
			"to-do": {
				gradient: "linear-gradient(135deg, #6C5CE7 0%, #A29BFE 100%)",
				badge: "#6C5CE7",
				light: "rgba(108, 92, 231, 0.1)",
			},
		};
		const key = guideName?.toLowerCase().split(" ")[0];
		return (
			configs[key] || {
				gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
				badge: "#6366F1",
				light: "rgba(99, 102, 241, 0.1)",
			}
		);
	};

	const colorConfig = getColorConfig(name, category);

	return (
		<Card
			elevation={0}
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				borderRadius: { xs: "14px", sm: "20px" },
				border: "1px solid rgba(0, 0, 0, 0.06)",
				overflow: "hidden",
				transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
				backgroundColor: "#fff",
				"&:hover": {
					transform: { sm: "translateY(-8px)" },
					boxShadow: { sm: "0 20px 40px rgba(0, 0, 0, 0.12)" },
					borderColor: { sm: "transparent" },
					"& .card-image": {
						transform: { sm: "scale(1.05)" },
					},
				},
			}}
		>
			{/* Image Container */}
			<Box sx={{ position: "relative", overflow: "hidden" }}>
				<CardMedia
					component="img"
					image={coverImage || "/placeholder-guide.jpg"}
					alt={name}
					className="card-image"
					sx={{
						width: "100%",
						aspectRatio: "1 / 1",
						objectFit: "cover",
						transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
					}}
				/>
				<CoverPlaceName guideId={id} />

				{/* Top badges */}
				<Box
					sx={{
						position: "absolute",
						top: { xs: 8, sm: 12 },
						left: { xs: 8, sm: 12 },
						right: { xs: 8, sm: 12 },
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
					}}
				>
					{/* ── Badge de acceso (único, bien visible) ── */}
					<Box>
						{isComingSoon ? (
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.5,
									backgroundColor: "rgba(99, 102, 241, 0.9)",
									backdropFilter: "blur(10px)",
									borderRadius: "10px",
									px: 1.5,
									py: 0.625,
									boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
								}}
							>
								<ScheduleIcon sx={{ fontSize: 14, color: "#fff" }} />
								<Typography
									sx={{
										fontSize: "0.75rem",
										fontWeight: 700,
										color: "#fff",
										lineHeight: 1,
									}}
								>
									Próximamente
								</Typography>
							</Box>
						) : isFreeOrTravelKit ? (
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.5,
									backgroundColor: "rgba(255,255,255,0.92)",
									backdropFilter: "blur(10px)",
									borderRadius: "10px",
									px: 1.5,
									py: 0.625,
									boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
								}}
							>
								<CheckCircleIcon sx={{ fontSize: 15, color: "#10B981" }} />
								<Typography
									sx={{
										fontSize: "0.75rem",
										fontWeight: 700,
										color: "#059669",
										lineHeight: 1,
									}}
								>
									Gratis
								</Typography>
							</Box>
						) : isLoggedIn && userHasAccess && !isFreeOrTravelKit ? (
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.5,
									backgroundColor: "rgba(255,255,255,0.92)",
									backdropFilter: "blur(10px)",
									borderRadius: "10px",
									px: 1.5,
									py: 0.625,
									boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
								}}
							>
								<LockOpenIcon sx={{ fontSize: 15, color: DASHBOARD.primary }} />
								<Typography
									sx={{
										fontSize: "0.75rem",
										fontWeight: 700,
										color: DASHBOARD.primaryDark,
										lineHeight: 1,
									}}
								>
									Desbloqueada
								</Typography>
							</Box>
						) : !isFreeOrTravelKit ? (
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.5,
									backgroundColor: "rgba(0,0,0,0.55)",
									backdropFilter: "blur(10px)",
									borderRadius: "10px",
									px: 1.5,
									py: 0.625,
									boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
								}}
							>
								<LockIcon
									sx={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}
								/>
								<Typography
									sx={{
										fontSize: "0.75rem",
										fontWeight: 700,
										color: "#fff",
										lineHeight: 1,
									}}
								>
									{price ? `${price}€` : "Bloqueada"}
								</Typography>
							</Box>
						) : null}
					</Box>

					{/* Corazón favorito */}
					{onToggleFavorite && (
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								onToggleFavorite(id);
							}}
							sx={{
								backgroundColor: "rgba(255, 255, 255, 0.92)",
								backdropFilter: "blur(10px)",
								width: 34,
								height: 34,
								boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
								"&:hover": {
									backgroundColor: "#fff",
								},
							}}
						>
							{isFavorite ? (
								<FavoriteIcon sx={{ fontSize: 18, color: "#F43F5E" }} />
							) : (
								<FavoriteBorderIcon sx={{ fontSize: 18, color: "#64748B" }} />
							)}
						</IconButton>
					)}
				</Box>

				{/* Duration badge: se oculta si el nombre Titan One va en la portada */}
				{days && !GUIDE_COVER_TITLES[id] && (
					<Box
						sx={{
							position: "absolute",
							bottom: { xs: 8, sm: 12 },
							left: { xs: 8, sm: 12 },
							display: "flex",
							alignItems: "center",
							gap: 0.5,
							backgroundColor: "rgba(0, 0, 0, 0.6)",
							backdropFilter: "blur(8px)",
							color: "#fff",
							px: { xs: 0.875, sm: 1.25 },
							py: { xs: 0.25, sm: 0.5 },
							borderRadius: { xs: "6px", sm: "8px" },
							fontSize: { xs: "0.688rem", sm: "0.75rem" },
							fontWeight: 600,
						}}
					>
						<AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
						{days} días
					</Box>
				)}
			</Box>

			<CardContent
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					p: { xs: 1.5, sm: 2.5 },
					"&:last-child": { pb: { xs: 1.5, sm: 2.5 } },
				}}
			>
				{/* Tags — hidden on mobile for compact layout */}
				{tags.length > 0 && (
					<Box
						sx={{
							display: { xs: "none", sm: "flex" },
							gap: 0.75,
							mb: 1.5,
							flexWrap: "wrap",
						}}
					>
						{tags.slice(0, 2).map((tag, index) => (
							<Chip
								key={index}
								label={tag}
								size="small"
								sx={{
									height: 24,
									fontSize: "0.688rem",
									fontWeight: 600,
									backgroundColor: colorConfig.light,
									color: colorConfig.badge,
									border: "none",
								}}
							/>
						))}
					</Box>
				)}

				{/* Title */}
				<Typography
					variant="h6"
					sx={{
						fontWeight: 700,
						color: "#1a1a2e",
						fontSize: { xs: "0.875rem", sm: "1.063rem" },
						lineHeight: 1.3,
						mb: { xs: 0.25, sm: 0.75 },
						display: "-webkit-box",
						WebkitLineClamp: 2,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					}}
				>
					{name}
				</Typography>

				{/* Description — hidden on mobile */}
				{description && (
					<Typography
						variant="body2"
						sx={{
							color: "#64748B",
							fontSize: "0.875rem",
							lineHeight: 1.5,
							mb: 2,
							flex: 1,
							display: { xs: "none", sm: "-webkit-box" },
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
						}}
					>
						{description}
					</Typography>
				)}

				{/* CTAs */}
				{isFreeOrTravelKit || userHasAccess ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: { xs: 1.25, sm: 1 },
							mt: "auto",
							pt: { xs: 1, sm: 0 },
						}}
					>
						{id !== "travelkit" && (onViewGuide || (isComingSoon && onComingSoonClick)) && (
							<Button
								variant="outlined"
								fullWidth
								startIcon={
									<VisibilityOutlinedIcon
										sx={{ fontSize: { xs: 16, sm: 18 } }}
									/>
								}
								onClick={(e) => {
									e.stopPropagation();
									if (isComingSoon && onComingSoonClick) {
										onComingSoonClick(guide);
									} else {
										onViewGuide(id);
									}
								}}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									fontSize: { xs: "0.813rem", sm: "0.875rem" },
									borderRadius: { xs: "10px", sm: "10px" },
									py: { xs: 0.875, sm: 1 },
									borderWidth: 2,
									borderColor: colorConfig.badge,
									color: colorConfig.badge,
									transition:
										"transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
									"&:hover": {
										borderWidth: 2,
										borderColor: colorConfig.badge,
										backgroundColor: colorConfig.light,
										transform: "translateY(-2px)",
										boxShadow: `0 4px 12px ${colorConfig.badge}30`,
									},
								}}
							>
								Ver guía
							</Button>
						)}
						<Button
							variant="contained"
							fullWidth
							endIcon={<EastIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
							onClick={() =>
								isComingSoon && onComingSoonClick
									? onComingSoonClick(guide)
									: onCustomizeGuide?.(id)
							}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								fontSize: { xs: "0.813rem", sm: "0.875rem" },
								background: colorConfig.gradient,
								borderRadius: { xs: "10px", sm: "10px" },
								py: { xs: 0.875, sm: 1 },
								px: { xs: 1.25, sm: 2 },
								minHeight: { xs: 44, sm: "auto" },
								boxShadow: `0 2px 12px ${colorConfig.badge}35`,
								transition:
									"transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease",
								"&:hover": {
									background: colorConfig.gradient,
									filter: "brightness(1.05)",
									transform: "translateY(-2px)",
									boxShadow: `0 6px 20px ${colorConfig.badge}50`,
								},
							}}
						>
							Usar guía
						</Button>
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: { xs: 1.25, sm: 1 },
							mt: "auto",
							pt: { xs: 1, sm: 0 },
						}}
					>
						{id !== "travelkit" && (onViewGuide || (isComingSoon && onComingSoonClick)) && (
							<Button
								variant="outlined"
								fullWidth
								startIcon={
									<VisibilityOutlinedIcon
										sx={{ fontSize: { xs: 16, sm: 18 } }}
									/>
								}
								onClick={(e) => {
									e.stopPropagation();
									if (isComingSoon && onComingSoonClick) {
										onComingSoonClick(guide);
									} else {
										onViewGuide(id);
									}
								}}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									fontSize: { xs: "0.813rem", sm: "0.875rem" },
									borderRadius: { xs: "10px", sm: "10px" },
									py: { xs: 0.875, sm: 1 },
									borderWidth: 2,
									borderColor: colorConfig.badge,
									color: colorConfig.badge,
									alignItems: "flex-start",
									"& .MuiButton-startIcon": { mt: 0.25 },
									transition:
										"transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
									"&:hover": {
										borderWidth: 2,
										borderColor: colorConfig.badge,
										backgroundColor: colorConfig.light,
										transform: "translateY(-2px)",
										boxShadow: `0 4px 12px ${colorConfig.badge}30`,
									},
								}}
							>
								Vista previa
							</Button>
						)}
						<Button
							variant="contained"
							size="small"
							fullWidth
							disabled={isPurchasing}
							startIcon={
								isPurchasing ? (
									<CircularProgress
										size={14}
										color="inherit"
										sx={{ color: "#fff" }}
									/>
								) : (
									<ShoppingCartIcon sx={{ fontSize: { xs: 16, sm: 16 } }} />
								)
							}
							onClick={() =>
								isComingSoon && onComingSoonClick
									? onComingSoonClick(guide)
									: onPurchaseGuide?.(guide)
							}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								fontSize: { xs: "0.75rem", sm: "0.813rem" },
								whiteSpace: { xs: "normal", sm: "nowrap" },
								lineHeight: 1.3,
								background: "#059669",
								borderRadius: "10px",
								py: { xs: 0.875, sm: 0.875 },
								px: { xs: 1.25, sm: 1.5 },
								minHeight: 42,
								boxShadow: "none",
								alignItems: "center",
								"& .MuiButton-startIcon": {
									display: "flex",
									alignItems: "center",
									mr: 0.5,
								},
								transition: "background-color 0.2s ease, filter 0.2s ease",
								"&:hover": {
									background: "#047857",
									boxShadow: "none",
									filter: "brightness(1.05)",
								},
							}}
						>
							{isPurchasing ? "Procesando..." : "Suscríbete o cómprala"}
						</Button>
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

const ExploreSection = ({
	guides = [],
	loading = false,
	onViewGuide,
	onCustomizeGuide,
	onPurchaseGuide,
	onComingSoonClick = null,
	purchasingGuideId = null,
	onSeeAll,
	user = null,
	onLogin = null,
	favoriteIds = [],
	onToggleFavorite = null,
	// Para página de favoritos: título distinto y sin tabs
	title,
	subtitle,
	hideTabs = false,
	hideSeeAllButton = false,
}) => {
	const [activeCategory, setActiveCategory] = useState("all");
	const [activeRegion, setActiveRegion] = useState("all");

	// Detectar si la categoría actual muestra destinos (para mostrar sub-filtros)
	const showRegionFilters =
		activeCategory === "destinations" || activeCategory === "all";

	// Detectar regiones disponibles a partir de los tags de las guías
	const availableRegions = React.useMemo(() => {
		const destinationGuides = guides.filter((g) => {
			const cat = g.category || "guide";
			return cat !== "utility" && cat !== "travelkit";
		});
		const regions = new Set();
		destinationGuides.forEach((g) => {
			(g.tags || []).forEach((tag) => {
				const t = tag.toLowerCase();
				if (
					t === "asia" ||
					t === "europa" ||
					t === "américa" ||
					t === "america"
				)
					regions.add(t === "américa" ? "america" : t);
			});
		});
		return regions;
	}, [guides]);

	const filteredGuides = hideTabs
		? guides
		: guides.filter((guide) => {
				const guideCategory = guide.category || "guide";

				// Filtro por tipo
				if (activeCategory === "destinations") {
					if (guideCategory === "utility" || guideCategory === "travelkit")
						return false;
				} else if (activeCategory === "utility") {
					// Utilidades incluye tanto utility como travelkit
					if (guideCategory !== "utility" && guideCategory !== "travelkit")
						return false;
				}

				// Filtro por región (solo aplica a destinos)
				if (
					activeRegion !== "all" &&
					showRegionFilters &&
					guideCategory !== "utility" &&
					guideCategory !== "travelkit"
				) {
					const guideTags = (guide.tags || []).map((t) =>
						t.toLowerCase().replace("é", "e"),
					);
					if (!guideTags.includes(activeRegion)) return false;
				}

				return true;
			});

	// Reset región cuando cambiamos de categoría
	const handleCategoryChange = (catId) => {
		setActiveCategory(catId);
		setActiveRegion("all");
	};

	return (
		<Box sx={{ mb: 5 }}>
			{/* Banner para usuarios no logueados */}
			{!user && !loading && guides.length > 0 && (
				<Box
					sx={{
						mb: { xs: 2, sm: 3 },
						p: { xs: 1.5, sm: 2 },
						borderRadius: { xs: "14px", sm: DASHBOARD.radiusCard },
						background: `linear-gradient(135deg, ${DASHBOARD.primaryTint} 0%, rgba(99, 102, 241, 0.06) 100%)`,
						border: `1px solid ${DASHBOARD.borderHover}`,
						display: "flex",
						alignItems: { xs: "flex-start", sm: "center" },
						justifyContent: "space-between",
						flexDirection: { xs: "column", sm: "row" },
						gap: { xs: 1.5, sm: 2 },
					}}
				>
					<Typography
						sx={{
							color: DASHBOARD.textPrimary,
							fontSize: { xs: "0.813rem", sm: "0.938rem" },
							fontWeight: 600,
							lineHeight: 1.4,
						}}
					>
						Regístrate gratis para ver o personalizar estas guías
					</Typography>
					<Button
						variant="contained"
						size="small"
						onClick={onLogin}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							fontSize: { xs: "0.813rem", sm: "0.875rem" },
							borderRadius: "10px",
							px: 2,
							py: 0.875,
							flexShrink: 0,
							width: { xs: "100%", sm: "auto" },
							backgroundColor: `${DASHBOARD.primary} !important`,
							color: "#fff !important",
							"&:hover": {
								backgroundColor: `${DASHBOARD.primaryDark} !important`,
							},
						}}
					>
						Registrarse gratis
					</Button>
				</Box>
			)}

			{/* Section Header */}
			<Box sx={{ mb: hideTabs ? 3 : 2.5 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 0.5,
					}}
				>
					<Typography
						variant="h5"
						sx={{
							fontWeight: 700,
							color: DASHBOARD.textPrimary,
							fontSize: { xs: "1.25rem", md: "1.5rem" },
							letterSpacing: "-0.02em",
							marginTop: { xs: "24px", md: 0 },
						}}
					>
						{title ?? "Guías listas para viajar"}
					</Typography>

					{!hideSeeAllButton && onSeeAll && (
						<Button
							variant="text"
							endIcon={<EastIcon sx={{ fontSize: 18 }} />}
							onClick={onSeeAll}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								fontSize: { xs: "0.813rem", sm: "0.875rem" },
								color: DASHBOARD.primary,
								border: "none !important",
								borderWidth: "0 !important",
								outline: "none",
								boxShadow: "none",
								borderRadius: DASHBOARD.radiusButton,
								px: { xs: 0.5, sm: 1.5 },
								py: { xs: 0.5, sm: 1 },
								minWidth: "auto",
								whiteSpace: "nowrap",
								backgroundColor: "transparent",
								"&:hover": {
									backgroundColor: DASHBOARD.primaryHover,
									color: DASHBOARD.primary,
									border: "none !important",
									borderWidth: "0 !important",
									boxShadow: "none",
								},
							}}
						>
							Ver todas
						</Button>
					)}
				</Box>
				{subtitle !== null && (
					<Typography
						sx={{
							color: DASHBOARD.textMuted,
							fontSize: { xs: "0.875rem", sm: "0.938rem" },
						}}
					>
						{subtitle ??
							"Itinerarios reales, organizados día a día para que solo tengas que disfrutar del viaje"}
					</Typography>
				)}
			</Box>

			{/* ── Category Tabs ── */}
			{!hideTabs && (
				<Box
					sx={{
						display: "flex",
						gap: { xs: 0.75, sm: 1 },
						mb: showRegionFilters && activeCategory !== "all" ? 0 : 3,
						flexWrap: "nowrap",
						overflowX: "auto",
						overflowY: "visible",
						// Espacio arriba para que el hover (translateY) no se corte
						pt: 0.5,
						mt: -0.5,
						"&::-webkit-scrollbar": { display: "none" },
						scrollbarWidth: "none",
					}}
				>
					{categories.map((category) => {
						const Icon = category.Icon;
						const isActive = activeCategory === category.id;
						return (
							<Button
								key={category.id}
								variant="text"
								disableElevation
								onClick={() => handleCategoryChange(category.id)}
								startIcon={
									<Icon
										sx={{
											fontSize: 16,
											color: isActive ? "#fff !important" : DASHBOARD.textMuted,
											transition: "color 0.2s",
										}}
									/>
								}
								sx={{
									textTransform: "none",
									fontWeight: 600,
									fontSize: "0.838rem",
									whiteSpace: "nowrap",
									flexShrink: 0,
									px: 2.25,
									py: 0.875,
									borderRadius: "50px",
									minWidth: "auto",
									color: isActive ? "#fff !important" : DASHBOARD.textMuted,
									background: isActive
										? `linear-gradient(135deg, ${DASHBOARD.primary} 0%, ${DASHBOARD.primaryDark} 100%)`
										: DASHBOARD.bgCard,
									border: isActive
										? "0 !important"
										: `1px solid ${DASHBOARD.border}`,
									boxShadow: isActive
										? `0 4px 12px ${DASHBOARD.primary}40`
										: "none",
									outline: "none",
									transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
									"&:focus, &:focus-visible": {
										outline: "none",
										...(isActive && { border: "0 !important" }),
									},
									"&:hover": {
										background: isActive
											? `linear-gradient(135deg, ${DASHBOARD.primaryDark} 0%, #4338ca 100%)`
											: DASHBOARD.primaryHover,
										color: isActive ? "#fff !important" : DASHBOARD.primary,
										border: isActive
											? "0 !important"
											: `1px solid ${DASHBOARD.borderHover}`,
										transform: "translateY(-1px)",
										boxShadow: isActive
											? `0 6px 16px ${DASHBOARD.primary}50`
											: DASHBOARD.shadowSm,
										"& .MuiButton-startIcon .MuiSvgIcon-root": {
											color: `${
												isActive ? "#fff !important" : DASHBOARD.primary
											}`,
										},
									},
									"& .MuiButton-startIcon .MuiSvgIcon-root": {
										color: `${
											isActive ? "#fff !important" : DASHBOARD.textMuted
										}`,
									},
									"& .MuiButton-startIcon": {
										mr: 0.5,
									},
								}}
							>
								{category.label}
							</Button>
						);
					})}
				</Box>
			)}

			{/* ── Region Sub-filters (solo para destinos) ── */}
			{!hideTabs && showRegionFilters && activeCategory === "destinations" && (
				<Box
					sx={{
						display: "flex",
						gap: 1,
						mt: 2,
						mb: 3,
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<PublicIcon
						sx={{
							fontSize: 18,
							color: DASHBOARD.textMuted,
							mr: 0.25,
						}}
					/>
					{regionFilters.map((region) => {
						const isActive = activeRegion === region.id;
						const isAvailable =
							region.id === "all" || availableRegions.has(region.id);
						return (
							<Chip
								key={region.id}
								label={`${region.label}`}
								onClick={() => setActiveRegion(region.id)}
								disabled={!isAvailable}
								size="small"
								sx={{
									fontWeight: isActive ? 700 : 500,
									fontSize: "0.813rem",
									height: 32,
									px: 0.5,
									borderRadius: "10px",
									backgroundColor: isActive
										? DASHBOARD.primary
										: "rgba(0, 0, 0, 0.04)",
									color: isActive ? "#fff" : DASHBOARD.textMuted,
									border: isActive ? "none" : "1px solid rgba(0,0,0,0.08)",
									cursor: isAvailable ? "pointer" : "default",
									transition: "all 0.2s ease",
									"&:hover": isAvailable
										? {
												backgroundColor: isActive
													? DASHBOARD.primaryDark
													: "rgba(99, 102, 241, 0.08)",
												color: isActive ? "#fff" : DASHBOARD.primary,
												borderColor: isActive
													? "transparent"
													: DASHBOARD.borderHover,
											}
										: {},
									"&.Mui-disabled": {
										opacity: 0.4,
									},
								}}
							/>
						);
					})}
				</Box>
			)}

			{/* Guides — 2 por fila en móvil (scroll vertical), grid en desktop */}
			<Grid container spacing={{ xs: 1.5, sm: 3 }}>
				{loading
					? Array.from({ length: 4 }).map((_, index) => (
							<Grid item xs={6} sm={6} lg={3} key={index}>
								<Card
									elevation={0}
									sx={{
										borderRadius: { xs: "14px", sm: DASHBOARD.radiusCard },
										overflow: "hidden",
										border: `1px solid ${DASHBOARD.border}`,
										height: "100%!important",
									}}
								>
									<Skeleton
										variant="rectangular"
										sx={{
											bgcolor: "rgba(0, 0, 0, 0.06)",
											aspectRatio: { xs: "4 / 5", sm: "1 / 1" },
											width: "100%",
										}}
									/>
									<CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
										<Skeleton
											variant="text"
											width="70%"
											height={18}
											sx={{ mb: 0.5 }}
										/>
										<Skeleton
											variant="rectangular"
											height={32}
											sx={{ borderRadius: "10px" }}
										/>
									</CardContent>
								</Card>
							</Grid>
						))
					: filteredGuides.map((guide, index) => (
							<Grid item xs={6} sm={6} lg={3} key={guide.id}>
								<Box
									sx={{
										animation: "fadeInUp 0.4s ease-out forwards",
										animationDelay: `${index * 60}ms`,
										opacity: 0,
										height: "100%!important",
									}}
								>
									<GuideCardDashboard
										guide={guide}
										onViewGuide={onViewGuide}
										onCustomizeGuide={onCustomizeGuide}
										onPurchaseGuide={onPurchaseGuide}
										onComingSoonClick={onComingSoonClick}
										purchasingGuideId={purchasingGuideId}
										isFavorite={favoriteIds.includes(guide.id)}
										onToggleFavorite={onToggleFavorite}
										isLoggedIn={!!user}
									/>
								</Box>
							</Grid>
						))}
			</Grid>

			{/* Empty State */}
			{!loading && filteredGuides.length === 0 && (
				<Box
					sx={{
						textAlign: "center",
						py: 8,
						px: 3,
						backgroundColor: DASHBOARD.bgCard,
						borderRadius: DASHBOARD.radiusCard,
						border: `2px dashed ${DASHBOARD.border}`,
					}}
				>
					<Box
						sx={{
							width: 80,
							height: 80,
							borderRadius: "50%",
							backgroundColor: DASHBOARD.primaryTint,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mx: "auto",
							mb: 3,
						}}
					>
						<AutoAwesomeIcon sx={{ fontSize: 36, color: DASHBOARD.primary }} />
					</Box>
					<Typography
						variant="h6"
						sx={{ color: DASHBOARD.textPrimary, fontWeight: 600, mb: 1 }}
					>
						No hay plantillas en esta categoría
					</Typography>
					<Typography sx={{ color: DASHBOARD.textMuted, fontSize: "0.938rem" }}>
						Prueba con otra categoría o explora todas las plantillas
					</Typography>
				</Box>
			)}
		</Box>
	);
};

export default ExploreSection;
